import {useCallback, useEffect, useRef, useState} from 'react';
import {REWARD_MODAL_TRANSITION_MS} from '../constants';
import {RewardModalKey} from '../types';

type RewardModalVisibleState = Record<RewardModalKey, boolean>;

const initialVisibleState: RewardModalVisibleState = {
  withdrawal: false,
  congratulation: false,
  cash: false,
};

export function useRewardModals() {
  const [visible, setVisible] =
    useState<RewardModalVisibleState>(initialVisibleState);
  const [closing, setClosing] = useState<
    Partial<Record<RewardModalKey, boolean>>
  >({});
  const closeTimerRefs = useRef<Partial<Record<RewardModalKey, number>>>({});

  const openRewardModal = useCallback((key: RewardModalKey) => {
    const closeTimer = closeTimerRefs.current[key];
    if (closeTimer != null) {
      window.clearTimeout(closeTimer);
      delete closeTimerRefs.current[key];
    }

    setClosing(prev => ({...prev, [key]: false}));
    setVisible(prev => ({...prev, [key]: true}));
  }, []);

  const closeRewardModal = useCallback(
    (key: RewardModalKey, onClosed?: () => void) => {
      const closeTimer = closeTimerRefs.current[key];
      if (closeTimer != null) {
        window.clearTimeout(closeTimer);
      }

      setClosing(prev => ({...prev, [key]: true}));
      closeTimerRefs.current[key] = window.setTimeout(() => {
        setVisible(prev => ({...prev, [key]: false}));
        setClosing(prev => ({...prev, [key]: false}));
        delete closeTimerRefs.current[key];
        onClosed?.();
      }, REWARD_MODAL_TRANSITION_MS);
    },
    [],
  );

  useEffect(() => {
    const closeTimers = closeTimerRefs.current;

    return () => {
      Object.values(closeTimers).forEach(timer => {
        if (timer != null) {
          window.clearTimeout(timer);
        }
      });
    };
  }, []);

  return {
    rewardModalVisible: visible,
    closingRewardModals: closing,
    openRewardModal,
    closeRewardModal,
  };
}
